// 📦 OntvangstBewerkModal — verwerkte ontvangst (leverancier of eigen productie) bekijken, bewerken of annuleren.
// Bewerken past de voorraad aan met het verschil (server berekent delta per regel).
import { useMemo, useState } from 'react';
import { X, Trash, Plus, FloppyDisk, Factory, Truck, Warning, MagnifyingGlass } from '@phosphor-icons/react';
import API from '@/lib/api';
import { fmtDec, pf } from '@/lib/numFmt';
import { toast } from 'sonner';

const fmt = (n) => `€ ${pf(n).toLocaleString('nl-BE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const toLocalInput = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const p = (x) => String(x).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
};

export default function OntvangstBewerkModal({ ontvangst, artikels = [], onClose, onSaved }) {
  const [type, setType] = useState(ontvangst.type || 'leverancier');
  const [leverancier, setLeverancier] = useState(ontvangst.leverancier || '');
  const [nota, setNota] = useState(ontvangst.nota || '');
  const [datum, setDatum] = useState(toLocalInput(ontvangst.datum));
  const [regels, setRegels] = useState(() => (ontvangst.regels || []).map((r, i) => ({ ...r, _k: `${r.art_id}|${r.combo_key || ''}|${i}`, _oud: pf(r.aantal) })));
  const [zoek, setZoek] = useState('');
  const [busy, setBusy] = useState('');
  const [bevestigAnnuleer, setBevestigAnnuleer] = useState(false);
  const alleenLezen = !!ontvangst.geannuleerd;

  const kandidaten = useMemo(() => {
    const q = zoek.trim().toLowerCase();
    if (q.length < 2) return [];
    const inLijst = new Set(regels.map(r => `${r.art_id}|${r.combo_key || ''}`));
    return artikels.filter(a => !inLijst.has(`${a.id}|`) && (`${a.art_code} ${a.omschrijving} ${a.ean || ''}`).toLowerCase().includes(q)).slice(0, 8);
  }, [zoek, artikels, regels]);

  const upd = (k, veld, v) => setRegels(p => p.map(r => r._k === k ? { ...r, [veld]: v } : r));
  const del = (k) => setRegels(p => p.filter(r => r._k !== k));
  const voegToe = (a) => {
    setRegels(p => [...p, { _k: `${a.id}||${Date.now()}`, _oud: 0, art_id: a.id, art_code: a.art_code, omschrijving: a.omschrijving, ean: a.ean || '', aantal: 1, aankoopprijs: pf(a.aankoopprijs), combo_key: null, variant_label: '' }]);
    setZoek('');
  };

  const totAantal = regels.reduce((s, r) => s + pf(r.aantal), 0);
  const totWaarde = regels.reduce((s, r) => s + pf(r.aantal) * pf(r.aankoopprijs), 0);
  const verwijderd = (ontvangst.regels || []).filter(o => !regels.some(r => r.art_id === o.art_id && (r.combo_key || '') === (o.combo_key || '')));
  const stockDelta = regels.reduce((s, r) => s + (pf(r.aantal) - r._oud), 0) - verwijderd.reduce((s, o) => s + pf(o.aantal), 0);

  const opslaan = async () => {
    if (regels.length === 0) { toast.error('Minstens 1 regel nodig — gebruik "Annuleer ontvangst" om alles terug te draaien'); return; }
    setBusy('save');
    try {
      const r = await API.put(`/inventaris/ontvangsten/${ontvangst.id}`, {
        type, leverancier, nota,
        datum: datum ? new Date(datum).toISOString() : ontvangst.datum,
        regels: regels.map(r => ({ art_id: r.art_id, aantal: pf(r.aantal), aankoopprijs: pf(r.aankoopprijs), combo_key: r.combo_key || null, variant_label: r.variant_label || '' })),
      });
      const n = r.data.wijzigingen?.length || 0;
      toast.success('Ontvangst bijgewerkt', { description: n ? `${n} regel(s) gewijzigd · voorraad aangepast` : 'Enkel gegevens gewijzigd, voorraad ongewijzigd' });
      onSaved?.(r.data.ontvangst);
      onClose();
    } catch (e) {
      toast.error('Opslaan mislukt', { description: e.response?.data?.detail || e.message });
    } finally { setBusy(''); }
  };

  const annuleer = async () => {
    setBusy('del');
    try {
      const r = await API.delete(`/inventaris/ontvangsten/${ontvangst.id}`);
      toast.success('Ontvangst geannuleerd', { description: `${r.data.teruggedraaid} regel(s) uit de voorraad teruggedraaid` });
      onSaved?.(null);
      onClose();
    } catch (e) {
      toast.error('Annuleren mislukt', { description: e.response?.data?.detail || e.message });
    } finally { setBusy(''); }
  };

  const Icon = type === 'productie' ? Factory : Truck;
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[92vh] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()} data-testid="ontvangst-bewerk-modal">
        <div className="bg-[#002FA7] text-white px-4 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-9 h-9 rounded-lg bg-white/10 flex items-center justify-center shrink-0"><Icon size={20} weight="duotone" /></div>
            <div className="min-w-0">
              <div className="font-bold text-sm leading-tight">{alleenLezen ? 'Geannuleerde ontvangst' : 'Ontvangst bewerken'} <span className="font-mono text-white/70 font-normal">{ontvangst.id}</span></div>
              <div className="text-[11px] text-white/70 leading-tight">Wijzigingen in aantallen worden meteen in de voorraad verrekend</div>
            </div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-white/15 rounded" data-testid="ontvangst-bewerk-close"><X size={18} /></button>
        </div>

        <div className="p-4 space-y-4 overflow-y-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-1">Bron</label>
              <div className="flex rounded-lg border border-gray-300 overflow-hidden">
                {[['leverancier', 'Leverancier', Truck], ['productie', 'Eigen productie', Factory]].map(([v, l, I]) => (
                  <button key={v} type="button" disabled={alleenLezen} onClick={() => setType(v)}
                    className={`flex-1 h-9 text-xs font-semibold inline-flex items-center justify-center gap-1 ${type === v ? 'bg-[#002FA7] text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                    data-testid={`ontvangst-bewerk-type-${v}`}><I size={13} /> {l}</button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-1">{type === 'productie' ? 'Productie / batch' : 'Leverancier'}</label>
              {type === 'productie'
                ? <div className="h-9 px-3 inline-flex items-center text-xs text-gray-500 bg-gray-50 border border-gray-200 rounded-lg w-full">Eigen productie</div>
                : <input value={leverancier} disabled={alleenLezen} onChange={e => setLeverancier(e.target.value)} placeholder="Naam leverancier" className="w-full h-9 px-3 text-sm border border-gray-300 rounded-lg focus:border-[#002FA7] focus:outline-none" data-testid="ontvangst-bewerk-leverancier" />}
            </div>
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-1">Datum</label>
              <input type="datetime-local" value={datum} disabled={alleenLezen} onChange={e => setDatum(e.target.value)} className="w-full h-9 px-2 text-sm border border-gray-300 rounded-lg focus:border-[#002FA7] focus:outline-none" data-testid="ontvangst-bewerk-datum" />
            </div>
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-1">Notitie / leverbon-nr</label>
              <input value={nota} disabled={alleenLezen} onChange={e => setNota(e.target.value)} placeholder="Optioneel" className="w-full h-9 px-3 text-sm border border-gray-300 rounded-lg focus:border-[#002FA7] focus:outline-none" data-testid="ontvangst-bewerk-nota" />
            </div>
          </div>

          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <table className="data-table text-xs">
              <thead>
                <tr>
                  <th>Art. code</th><th>Omschrijving</th>
                  <th className="right">Was</th><th className="right">Aantal</th><th className="right">Aankoop</th><th className="right">Waarde</th><th className="right">Stock</th>
                  {!alleenLezen && <th style={{ width: 36 }}></th>}
                </tr>
              </thead>
              <tbody>
                {regels.map(r => {
                  const d = pf(r.aantal) - r._oud;
                  return (
                    <tr key={r._k} data-testid={`ontvangst-bewerk-rij-${r.art_code}`}>
                      <td className="font-mono font-bold">{r.art_code}{r.combo_key && <span className="ml-1 text-[9px] bg-blue-100 text-blue-800 px-1 rounded font-normal">VAR</span>}</td>
                      <td>{r.omschrijving}{r.variant_label && <div className="text-[10px] text-blue-700 font-mono">{r.variant_label}</div>}</td>
                      <td className="right text-gray-400">{r._oud ? fmtDec(r._oud) : '—'}</td>
                      <td className="right">
                        <input type="text" inputMode="decimal" disabled={alleenLezen} value={fmtDec(r.aantal)} onChange={e => upd(r._k, 'aantal', e.target.value.replace(',', '.'))}
                          className="w-20 px-2 py-1 border border-gray-300 rounded text-right font-bold focus:border-[#002FA7] focus:outline-none" data-testid={`ontvangst-bewerk-aantal-${r.art_code}`} />
                      </td>
                      <td className="right">
                        <input type="text" inputMode="decimal" disabled={alleenLezen} value={fmtDec(r.aankoopprijs)} onChange={e => upd(r._k, 'aankoopprijs', e.target.value.replace(',', '.'))}
                          className="w-20 px-2 py-1 border border-gray-300 rounded text-right focus:border-[#002FA7] focus:outline-none" />
                      </td>
                      <td className="right font-bold">{fmt(pf(r.aantal) * pf(r.aankoopprijs))}</td>
                      <td className={`right font-mono font-bold ${d > 0 ? 'text-[#047857]' : d < 0 ? 'text-[#DC2626]' : 'text-gray-300'}`}>{d === 0 ? '±0' : (d > 0 ? '+' : '') + fmtDec(d)}</td>
                      {!alleenLezen && <td className="right"><button onClick={() => del(r._k)} className="p-1 text-red-500 hover:bg-red-50 rounded" title="Regel verwijderen (voorraad wordt teruggedraaid)"><Trash size={14} /></button></td>}
                    </tr>
                  );
                })}
                {verwijderd.map(o => (
                  <tr key={`del-${o.art_id}-${o.combo_key || ''}`} className="opacity-60 line-through">
                    <td className="font-mono">{o.art_code}</td><td>{o.omschrijving}</td>
                    <td className="right">{fmtDec(o.aantal)}</td><td className="right">0</td><td className="right">—</td><td className="right">—</td>
                    <td className="right font-mono font-bold text-[#DC2626] no-underline">−{fmtDec(o.aantal)}</td>{!alleenLezen && <td></td>}
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-gray-50 font-bold">
                  <td colSpan={3} className="text-gray-600">{regels.length} regel(s)</td>
                  <td className="right">{fmtDec(totAantal)}</td><td></td><td className="right">{fmt(totWaarde)}</td>
                  <td className={`right font-mono ${stockDelta > 0 ? 'text-[#047857]' : stockDelta < 0 ? 'text-[#DC2626]' : 'text-gray-400'}`} data-testid="ontvangst-bewerk-stockdelta">{stockDelta === 0 ? '±0' : (stockDelta > 0 ? '+' : '') + fmtDec(stockDelta)}</td>
                  {!alleenLezen && <td></td>}
                </tr>
              </tfoot>
            </table>
          </div>

          {!alleenLezen && (
            <div className="relative">
              <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input value={zoek} onChange={e => setZoek(e.target.value)} placeholder="Regel toevoegen: zoek op artikelcode of omschrijving…" className="w-full h-10 pl-9 pr-3 text-sm bg-gray-50 border border-gray-300 rounded-lg focus:bg-white focus:border-[#002FA7] focus:outline-none" data-testid="ontvangst-bewerk-zoek" />
              {kandidaten.length > 0 && (
                <div className="absolute z-20 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-xl max-h-64 overflow-auto">
                  {kandidaten.map(a => (
                    <button key={a.id} type="button" onMouseDown={e => { e.preventDefault(); voegToe(a); }} className="w-full text-left px-3 py-2 flex items-center gap-3 hover:bg-gray-50 border-b border-gray-100 last:border-0" data-testid={`ontvangst-bewerk-kandidaat-${a.art_code}`}>
                      <span className="font-mono font-bold text-[#002FA7] text-xs w-28 truncate">{a.art_code}</span>
                      <span className="text-sm text-gray-800 truncate flex-1">{a.omschrijving}</span>
                      <Plus size={14} className="text-gray-400" />
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {(ontvangst.wijzigingen || []).length > 0 && (
            <div className="text-[11px] text-gray-500">
              <div className="font-bold uppercase tracking-wider text-[10px] text-gray-400 mb-1">Eerdere wijzigingen</div>
              {ontvangst.wijzigingen.slice(-5).reverse().map((w, i) => (
                <div key={i}>{new Date(w.op).toLocaleString('nl-BE', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' })} · {w.door} · {w.samenvatting || `${w.regels_gewijzigd} regel(s)`}</div>
              ))}
            </div>
          )}
        </div>

        <div className="px-4 py-3 border-t border-gray-100 bg-gray-50 flex items-center justify-between gap-2 flex-wrap">
          {alleenLezen ? <div className="text-xs text-gray-500">Geannuleerd op {new Date(ontvangst.geannuleerd_op).toLocaleString('nl-BE')}</div> : (
            bevestigAnnuleer ? (
              <div className="flex items-center gap-2 text-xs">
                <Warning size={16} className="text-red-600" /> Alle {regels.length} regel(s) uit de voorraad terugdraaien?
                <button onClick={annuleer} disabled={!!busy} className="btn btn-sm bg-red-600 text-white hover:bg-red-700" data-testid="ontvangst-annuleer-bevestig">{busy === 'del' ? 'Bezig…' : 'Ja, annuleer'}</button>
                <button onClick={() => setBevestigAnnuleer(false)} className="btn btn-sm bg-white border border-gray-300">Nee</button>
              </div>
            ) : (
              <button onClick={() => setBevestigAnnuleer(true)} className="text-xs text-red-600 hover:underline inline-flex items-center gap-1" data-testid="ontvangst-annuleer-btn"><Trash size={13} /> Annuleer ontvangst (alles terugdraaien)</button>
            )
          )}
          <div className="flex gap-2">
            <button onClick={onClose} className="btn btn-sm bg-white border border-gray-300 text-gray-700 hover:bg-gray-50">Sluiten</button>
            {!alleenLezen && (
              <button onClick={opslaan} disabled={!!busy} className="btn btn-sm bg-[#002FA7] text-white hover:bg-[#00247f] inline-flex items-center gap-1.5 disabled:opacity-50" data-testid="ontvangst-bewerk-opslaan">
                <FloppyDisk size={14} weight="bold" /> {busy === 'save' ? 'Bezig…' : 'Opslaan'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
